// @ts-nocheck
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Check, ChevronLeft, ChevronRight, Loader2, Pause, Play, User } from 'lucide-react';
import { Mars, Venus } from './agentIcons';
import { cn } from '@/lib/utils/cn';
import CollapsibleSection from './CollapsibleSection';
import HeadingInfo from './HeadingInfo';
import { getVoices } from '@/service/aiAgentApi';

function normalizeVoice(v: any) {
  const rawGender = String(v?.gender ?? v?.voice_gender ?? v?.sex ?? '')
    .trim()
    .toLowerCase();
  const gender = rawGender.startsWith('f') ? 'female' : rawGender.startsWith('m') ? 'male' : '';
  const sampleUrl =
    v?.voice_url ??
    v?.sample_url ??
    v?.sample_audio_url ??
    v?.sample_audio ??
    v?.audio_url ??
    v?.preview_url ??
    v?.sample ??
    '';
  return { ...v, gender, sample_url: typeof sampleUrl === 'string' ? sampleUrl : '' };
}

const GENDER_UI = {
  female: {
    label: 'Female',
    Icon: Venus,
    badge: 'bg-pink-100 text-pink-600 dark:bg-pink-950/60 dark:text-pink-300',
  },
  male: {
    label: 'Male',
    Icon: Mars,
    badge: 'bg-blue-100 text-blue-600 dark:bg-blue-950/60 dark:text-blue-300',
  },
  unknown: {
    label: 'Voice',
    Icon: User,
    badge: 'bg-muted text-muted-foreground',
  },
};


const AVATAR_STYLES = [
  { grad: 'from-amber-400 to-orange-500', ring: 'ring-amber-200 dark:ring-amber-900' },
  { grad: 'from-emerald-400 to-teal-500', ring: 'ring-emerald-200 dark:ring-emerald-900' },
  { grad: 'from-rose-400 to-pink-500', ring: 'ring-rose-200 dark:ring-rose-900' },
  { grad: 'from-indigo-400 to-blue-500', ring: 'ring-indigo-200 dark:ring-indigo-900' },
  { grad: 'from-violet-400 to-purple-500', ring: 'ring-violet-200 dark:ring-violet-900' },
  { grad: 'from-sky-400 to-cyan-500', ring: 'ring-sky-200 dark:ring-sky-900' },
];

function avatarStyleFor(key: string) {
  const s = String(key || '');
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_STYLES[hash % AVATAR_STYLES.length];
}

function initialsFor(name: string) {
  const clean = String(name || '').trim();
  if (!clean) return '?';
  const parts = clean.split(/\s+/);
  return parts.length > 1
    ? (parts[0][0] + parts[1][0]).toUpperCase()
    : clean.slice(0, 2).toUpperCase();
}

export default function VoiceIdSection({ value, onChange }: any) {
  const [voices, setVoices] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updateScrollState) : null;
    ro?.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro?.disconnect();
    };
  }, [loading, voices, updateScrollState]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || loading) return;
    const tile = el.querySelector('[aria-checked="true"]') as HTMLElement | null;
    if (!tile) return;
    const tileEnd = tile.offsetLeft + tile.offsetWidth;
    if (tileEnd > el.clientWidth) el.scrollLeft = Math.max(0, tile.offsetLeft - 8);
  }, [loading, voices]);

  const scrollTiles = (direction: -1 | 1) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' });
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getVoices()
      .then((list) => {
        if (cancelled) return;
        setVoices(Array.isArray(list) ? list.map(normalizeVoice) : []);
        setError('');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(typeof err === 'string' ? err : 'Failed to load voices');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const stopAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.onplaying = null;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audioRef.current = null;
    }
    setPlayingId(null);
    setLoadingId(null);
  }, []);

  useEffect(() => stopAudio, [stopAudio]);

  const togglePlay = (voice: any, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const url = voice?.sample_url;
    if (!url) return;
    const id = String(voice.id);
    if (playingId === id || loadingId === id) {
      stopAudio();
      return;
    }

    stopAudio();
    const audio = new Audio(url);
    audioRef.current = audio;
    setLoadingId(id);
    audio.onplaying = () => {
      setLoadingId(null);
      setPlayingId(id);
    };
    audio.onended = () => stopAudio();
    audio.onerror = () => {
      stopAudio();
      toast.error('Unable to play the voice sample');
    };
    audio.play().catch(() => {
      if (audioRef.current !== audio) return;
      stopAudio();
      toast.error('Unable to play the voice sample');
    });
  };

  return (
    <CollapsibleSection title="Voice ID" required>
      <div className="space-y-4">
        <p className="text-sm font-semibold text-foreground">Voice ID</p>
        <p className="mt-1 text-xs text-muted-foreground">
          Pick a voice for your agent — tap the play badge to preview a sample first.
        </p>

        <div className="space-y-2">
          <Label className="flex items-center gap-1.5">
            Voice <span className="text-destructive">*</span>
            <HeadingInfo text="The text-to-speech voice the agent speaks with." />
          </Label>

          {loading && (
            <div className="flex gap-3 overflow-hidden">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="h-32 w-28 shrink-0 animate-pulse rounded-xl border border-border bg-muted/50" />
              ))}
            </div>
          )}

          {!loading && !error && voices.length === 0 && (
            <p className="rounded-md border border-dashed border-border p-3 text-xs italic text-muted-foreground">
              No voices available.
            </p>
          )}

          {!loading && !error && voices.length > 0 && (
            <>
              <div
                ref={scrollerRef}
                role="radiogroup"
                aria-label="Voice"
                className="relative flex snap-x snap-mandatory gap-3 overflow-x-auto scroll-smooth p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              >
                {voices.map((v) => {
                  const selected = String(v.id) === String(value);
                  const style = avatarStyleFor(String(v.id ?? v.name));
                  const genderUi = GENDER_UI[v.gender] || GENDER_UI.unknown;
                  const GenderIcon = genderUi.Icon;
                  const hasSample = !!v.sample_url;
                  const isPlaying = playingId === String(v.id);
                  const isLoadingSample = loadingId === String(v.id);

                  return (
                    <button
                      key={v.id}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onChange(String(v.id))}
                      title={v.name}
                      className={cn(
                        'relative flex w-28 shrink-0 snap-start flex-col items-center gap-2 rounded-xl border-2 px-2 pb-3 pt-4 text-center transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50',
                        selected
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-border bg-card hover:border-primary/40 hover:bg-muted/50',
                      )}
                    >
                      {selected && (
                        <span data-tile="check" className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground">
                          <Check className="h-3 w-3" />
                        </span>
                      )}

                      <span className="relative">
                        <span
                          className={cn(
                            'flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br text-base font-bold uppercase text-white shadow-sm ring-4',
                            style.grad,
                            style.ring,
                          )}
                        >
                          {initialsFor(v.name)}
                        </span>

                        {v.gender && (
                          <span
                            data-tile="gender-badge"
                            className={cn(
                              'absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full ring-2 ring-card',
                              genderUi.badge,
                            )}
                            title={genderUi.label}
                          >
                            <GenderIcon className="h-2.5 w-2.5" />
                          </span>
                        )}

                        <button
                          type="button"
                          onClick={(e) => togglePlay(v, e)}
                          disabled={!hasSample}
                          title={
                            !hasSample
                              ? 'No sample available'
                              : isPlaying || isLoadingSample
                                ? 'Stop sample'
                                : 'Play sample'
                          }
                          aria-label={isPlaying ? `Stop ${v.name} sample` : `Play ${v.name} sample`}
                          className={cn(
                            'absolute -bottom-1 -left-1 flex h-6 w-6 items-center justify-center rounded-full text-white shadow ring-2 ring-card transition-colors',
                            hasSample
                              ? 'bg-primary hover:bg-primary/90'
                              : 'cursor-not-allowed bg-muted-foreground/40',
                          )}
                        >
                          {isLoadingSample ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : isPlaying ? (
                            <Pause className="h-3 w-3" />
                          ) : (
                            <Play className="h-3 w-3 translate-x-px" />
                          )}
                        </button>
                      </span>

                      <span
                        data-tile="name"
                        className={cn(
                          'line-clamp-1 max-w-full text-sm font-medium leading-tight',
                          selected ? 'text-primary' : 'text-foreground',
                        )}
                      >
                        {v.name}
                      </span>
                      {v.gender && (
                        <span data-tile="gender-chip" className="hidden items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold">
                          <GenderIcon className="h-3 w-3" />
                          {genderUi.label}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {(canScrollLeft || canScrollRight) && (
                <div className="flex items-center justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => scrollTiles(-1)}
                    disabled={!canScrollLeft}
                    aria-label="Previous voices"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => scrollTiles(1)}
                    disabled={!canScrollRight}
                    aria-label="Next voices"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </>
          )}

          {error && <p className="text-xs text-destructive">{error}</p>}
        </div>
      </div>
    </CollapsibleSection>
  );
}
